import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import NewItemDialog from "../modal/NewItemDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { isAxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { Textarea } from "../ui/textarea";
import { useCreateAudiovisual } from "@/api/audiovisual/audiovisual";
import {
  appendNoDuplicateRecordNumberRefine,
  audiovisualSchema,
} from "@/api/audiovisual/audiovisual-validation";

const schema = appendNoDuplicateRecordNumberRefine(
  audiovisualSchema.pick({
    audiovisuelDescriptionNumeroEnregistrement: true,
    audiovisuelDescriptionTitre: true,
  }),
);
type FormType = z.infer<typeof schema>;

type NewAudiovisualDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const NewAudiovisualDialog = ({
  onOpenChange,
  open,
}: NewAudiovisualDialogProps) => {
  const { t } = useTranslation();
  const form = useForm<FormType>({
    resolver: zodResolver(schema),
    defaultValues: {
      audiovisuelDescriptionNumeroEnregistrement: "",
      audiovisuelDescriptionTitre: "",
    },
  });
  const { mutateAsync } = useCreateAudiovisual();
  const navigate = useNavigate();

  const onFormSubmit = async (data: FormType) => {
    try {
      const result = await mutateAsync(data);
      navigate(`/edit/audiovisual/${result.id}`);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("audiovisuelDescriptionNumeroEnregistrement", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw err;
    }
  };

  return (
    <NewItemDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("createAaudiovisual")}
      onFormSubmit={onFormSubmit}
      form={form}
    >
      <FormField
        control={form.control}
        name="audiovisuelDescriptionNumeroEnregistrement"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>
              {t("audiovisuelDescriptionNumeroEnregistrement")}
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="audiovisuelDescriptionTitre"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("audiovisuelDescriptionTitre")}</FormLabel>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </NewItemDialog>
  );
};

export default NewAudiovisualDialog;
